import * as AgmNames from "./const/agm-names";
import { default as CallbackRegistryFactory, CallbackRegistry, UnsubscribeFunction } from "callback-registry";
import { Configuration, InstanceProps, WindowContext } from "./types";
import AppManagerImpl from "./app-manager";
import { Glue42 } from "../../glue";
import { Glue42Core } from "@glue42/core";
import { isUndefinedOrNull } from "../activity/helpers/util";
import { validate } from "./helper";

export default class InstanceImpl implements Glue42.AppManager.Instance {
    private _registry: CallbackRegistry = CallbackRegistryFactory();

    private _startUpContext: WindowContext;
    private _title: string;
    private _isActivityInstance: boolean;
    private _activityId: string;
    private _inActivity: boolean;
    private _isActivityOwner: boolean;
    private _agmInstance: Partial<Glue42Core.AGM.Instance>;
    private _unsubscribeInstanceAgmServerReady: UnsubscribeFunction;
    private _unsubscribeInstanceStopped: UnsubscribeFunction;

    constructor(
        private _id: string,
        private _appName: string,
        private _appManager: AppManagerImpl,
        private _agm: Glue42Core.AGM.API,
        private _activities: Glue42.Activities.API,
        private _windows: Glue42.Windows.API,
        startFailed: boolean,
        private _configuration: () => Configuration
    ) {
        if (startFailed) {
            return;
        }

        this._unsubscribeInstanceStopped = this._appManager.onInstanceStopped((instance: Glue42.AppManager.Instance) => {
            if (instance.id !== this._id) {
                return;
            }

            this._registry.execute("stopped", instance);
        });

        this._unsubscribeInstanceAgmServerReady = this._appManager.onInstanceAgmServerReady((instance) => {
            if (instance.id !== this._id) {
                return;
            }

            this._registry.execute("agmReady", instance);
        });
    }

    get id(): string { return this._id; }

    get application(): Glue42.AppManager.Application { return this._appManager.application(this._appName); }

    get activity(): Glue42.Activities.Activity {
        if (!this._activities) {
            throw new Error("This method requires glue.activities library to be enabled.");
        }
        return this._activities.all.instances.get()
            .filter((activityInstance: Glue42.Activities.Activity) => activityInstance.id === this._activityId)[0];
    }

    get isActivityOwner(): boolean { return this._isActivityOwner; }

    // this property returns a list of activity window instances (not activity instances)
    get activityInstances(): Glue42.AppManager.Instance[] {
        return this._appManager.instances().filter((i) =>
            i.application.type !== "activity" && // GD2 backward compatibility -  do not show activity instances into the activity window instances list
            i.activityId && // glue js 2 and 3 bug and breaking change - they will show non activity window instances as activity in case of activityId === undefined
            i.activityId === this._activityId);
    }

    get activityOwnerInstance(): Glue42.AppManager.Instance {
        if (!this._activityId) {
            return undefined;
        }
        return this.activityInstances.filter((inst) => (inst as InstanceImpl)?.isActivityOwner)[0];
    }

    get window(): Glue42.Windows.GDWindow {
        if (!this._windows) {
            throw new Error("This method requires glue.windows library to be enabled.");
        }

        let win = this._windows.list().filter((w) => w.id === this._id)[0];
        if (!win && this._activities && this.activity && this.activityOwnerInstance) {
            // This is covering GD3 activity instance
            win = this.activityOwnerInstance.window;
        }

        return win;
    }

    get context(): WindowContext {
        return this._startUpContext ?? this.window?.context ?? {};
    }

    get title(): string { return this._title; }

    get isActivityInstance(): boolean { return this._isActivityInstance; }

    get activityId(): string { return this._activityId; }

    get inActivity(): boolean { return this._inActivity; }

    get isSingleWindowApp(): boolean { return !this._inActivity; }

    get agm(): Partial<Glue42Core.AGM.Instance> {
        return this._agmInstance;
    }

    public onAgmReady(callback: (instance: Glue42.AppManager.Instance) => any) {
        validate(callback, this._configuration());
        if (this._agmInstance) {
            setTimeout(() => {
                this._registry.execute("agmReady", this);
            }, 0);
        }
        return this._registry.add("agmReady", callback);
    }

    public onStopped(callback: (instance: Glue42.AppManager.Instance) => any) {
        validate(callback, this._configuration());
        return this._registry.add("stopped", callback);
    }

    // uses this.window and waits for the window to appear if needed
    // we can not remove this.window for legacy reasons
    public getWindow(): Promise<Glue42.Windows.GDWindow> {
        return new Promise((resolve, reject) => {
            // try to get it the normal way
            const result = this.window;
            if (result) {
                resolve(result);
                return;
            }

            // if we can not find it directly wait for it to appear
            const done = (error?: Error, window?: Glue42.Windows.GDWindow) => {
                if (error) {
                    reject(error);
                }

                if (window) {
                    resolve(window);
                }

                setTimeout(() => {
                    clearTimeout(timeout);
                    unsub();
                }, 0);
            };

            // if not found for 30 sec reject
            const timeout = setTimeout(() => {
                done(new Error(`can not find a window with id ${this._id}`));
            }, 30000);

            const unsub = this._windows.onWindowAdded((w) => {
                if (w.id === this._id) {
                    done(undefined, w);
                }
            });
        });
    }

    public updateFromProps(props: InstanceProps): void {
        this._startUpContext = props.Context;
        this._title = props.Title;
        this._isActivityInstance = false;
        if (props.ActivityId && props.ActivityId !== "") {
            this._activityId = props.ActivityId;
            this._isActivityInstance = true;
        }
        this._isActivityOwner = props.IsActivityOwner;
        if (!this._activityId && this._startUpContext && this._startUpContext.activityId) {
            this._activityId = this._startUpContext.activityId;
        }
        this._inActivity = Boolean(this._activityId);

        this.updateAgmInstanceFromProps(props);
    }

    public updateAgmInstanceFromProps(props: InstanceProps) {
        if (!props.AgmServers) {
            return;
        }

        const agmInstances: Glue42Core.AGM.Instance[] = props.AgmServers ;
        if (agmInstances && agmInstances.length > 0 && !isUndefinedOrNull(agmInstances[0])) {
            this._agmInstance = agmInstances[0];
        }
    }

    public stop(): Promise<void> {
        return new Promise((resolve, reject) => {
            let idToResolve = this._id;
            if (this.isActivityOwner) {
                idToResolve = this.activityId;
            }
            const unsubscribe = this._appManager.onInstanceStopped((instance) => {
                if (instance.id === idToResolve) {
                    unsubscribe();
                    resolve();
                }
            });

            this._agm.invoke(
                AgmNames.StopApplicationMethodName,
                {
                    Name: this._appName,
                    Id: this._id
                })
                .then(() => {
                    if (this._appManager.mode === "startOnly") {
                        this._appManager.handleInstanceStopped({
                            Name: this._appName,
                            Id: this.id
                        });
                        // in startOnly mode, resolve stop as soon as we receive
                        resolve();
                    }
                })
                .catch((err: any) => reject(err));
        });
    }

    public activate(): Promise<Glue42Core.AGM.InvocationResult<any>> {
        return this._agm.invoke(AgmNames.ActivateApplicationMethodName, { Name: this._appName, Id: this._id });
    }

    public done() {

        // remove all handlers
        this._registry.clear();
        // remove subscriptions
        this._unsubscribeInstanceAgmServerReady();
        this._unsubscribeInstanceStopped();
    }

    public getContext(): Promise<object> {
        return Promise.resolve(this.context);
    }
}
